// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
//
// The landing view, and the one with a job to do.
//
// A person arriving here is holding a thing and wants to know about that thing.
// They have a STRING — a brand from a box, a model, a barcode — not a question
// about distributions. So search is the front door and every chart is reachable
// from it, rather than the other way round.

import { getBundle } from '../data';
import { esc, num, shortDate, truncEsc } from '../format';
import { openRecall } from '../components/recallDrawer';
import { currentRoute, setParams, navigate } from '../router';
import { glossaryButton } from '../glossary';
import type { Recall } from '../types';
// The same normalisation the pipeline uses for the supplier leaderboard. Sharing
// the function is what lets a leaderboard row filter to EXACTLY its own rows.
import { normaliseSupplier } from '../../pipeline/parse.mjs';

const PAGE = 40;

export function renderSearch(host: HTMLElement): void {
  const { recalls, meta, hazards, hazardById, stats } = getBundle();
  const route = currentRoute();
  let query = route.params.get('q') ?? '';
  let hazardFilter = Number(route.params.get('h') ?? 0);
  /*
   * STRUCTURED filters, not free text.
   *
   * Charts used to drill down by navigating to a text search for the category's
   * NAME or the supplier's display string. That reliably found a DIFFERENT
   * number of recalls than the block just clicked: the category label is in the
   * search haystack only when it survived truncation, and a supplier's display
   * name is one of several spellings the leaderboard had merged. Filtering on
   * the id reproduces the clicked figure by construction.
   */
  const catFilter = Number(route.params.get('c') ?? 0);
  const supplierFilter = route.params.get('s') ?? '';
  const fromDays = route.params.get('from') === null ? null : Number(route.params.get('from'));
  const toDays = route.params.get('to') === null ? null : Number(route.params.get('to'));
  let shown = PAGE;

  host.innerHTML = `
    <div class="view-head">
      <h1>Has it been recalled?</h1>
      <p>Search every consumer product recall the ACCC has published — ${num(meta.accc.total)} of them,
      from ${meta.accc.firstYear} to ${meta.accc.lastYear} — by product, brand, ${glossaryButton('supplier', 'supplier')},
      retailer or ${glossaryButton('pra', 'PRA number')}. Vehicles are a separate register:
      <button type="button" class="glossary-link" id="to-vehicles">search vehicle recalls</button>.</p>
    </div>

    <div class="controls">
      <div class="control grow" style="flex:1 1 320px">
        <label class="field-label" for="q">Product, brand, retailer or PRA number</label>
        <input type="search" id="q" placeholder="e.g. pram, Kmart, button battery, 2026/20936"
               autocomplete="off" spellcheck="false" value="${esc(query)}" />
      </div>
      <div class="control">
        <label class="field-label" for="haz">Hazard</label>
        <select id="haz">
          <option value="0">Any hazard</option>
          ${hazards.map((h) => `<option value="${h.id}"${h.id === hazardFilter ? ' selected' : ''}>${esc(h.label)}</option>`).join('')}
        </select>
      </div>
    </div>

    <div id="result-summary" class="small muted" style="margin-bottom:var(--space-md)" role="status" aria-live="polite"></div>
    <div id="results" class="stack"></div>
    <div id="more-wrap" style="margin-top:var(--space-lg);text-align:center"></div>

    <div class="note caveat" style="margin-top:var(--space-2xl)">
      <strong>Not finding it does not mean it is safe.</strong> It means no recall has been published for it.
      Recalls also cover specific batches and date ranges, so a listed product may or may not include your
      item — check the notice, and contact the supplier if you are unsure.
    </div>
  `;

  const input = host.querySelector<HTMLInputElement>('#q')!;
  const hazSel = host.querySelector<HTMLSelectElement>('#haz')!;
  const results = host.querySelector<HTMLElement>('#results')!;
  const summary = host.querySelector<HTMLElement>('#result-summary')!;
  const moreWrap = host.querySelector<HTMLElement>('#more-wrap')!;

  host.querySelector('#to-vehicles')!.addEventListener('click', () => navigate('vehicles'));

  function matches(): Recall[] {
    const q = query.trim().toLowerCase();
    const words = q ? q.split(/\s+/).filter(Boolean) : [];
    return recalls.filter((r) => {
      if (hazardFilter && r.hazard !== hazardFilter) return false;
      if (catFilter && !r.cats.includes(catFilter)) return false;
      if (supplierFilter && normaliseSupplier(r.supplier) !== supplierFilter) return false;
      if (fromDays != null) {
        if (r.onSaleDays == null) return false;
        if (r.onSaleDays < fromDays) return false;
        if (toDays != null && toDays >= 0 && r.onSaleDays >= toDays) return false;
      }
      if (!words.length) return true;
      const hay = `${r.title} ${r.supplier} ${r.haystack}`.toLowerCase();
      return words.every((w) => hay.includes(w));
    });
  }

  function draw(): void {
    const found = matches();
    const q = query.trim();

    const active = q || hazardFilter || catFilter || supplierFilter || fromDays != null;
    summary.textContent = active
      ? `${num(found.length)} matching recall${found.length === 1 ? '' : 's'}`
      : `Showing the most recent of ${num(recalls.length)} recalls — start typing to search`;

    if (!found.length) {
      results.innerHTML = `
        <div class="empty-state">
          <p><strong>No published recall matches “${esc(q)}”.</strong></p>
          <p class="small" style="max-width:52ch;margin:var(--space-md) auto 0">
            Nothing here matched those words in a recall's title, supplier, retailers, product description
            or hazard description. That is <strong>not</strong> the same as the product being safe, and it is
            not a statement about what the ACCC has published — only about what this index could match.
            Try a shorter search (a brand on its own), check the
            <a href="https://www.productsafety.gov.au/recalls" target="_blank" rel="noopener">official register</a>,
            or report an unsafe product to the
            <a href="https://www.productsafety.gov.au/consumers/respond-to-recalls-and-unsafe-products/report-an-unsafe-product" target="_blank" rel="noopener">ACCC</a>.
          </p>
        </div>`;
      moreWrap.innerHTML = '';
      return;
    }

    // Newest first: a person searching a product wants the current notice.
    const sorted = [...found].sort((a, b) => (b.date ?? '').localeCompare(a.date ?? ''));
    const page = sorted.slice(0, shown);

    results.innerHTML = '';
    for (const r of page) {
      const haz = hazardById.get(r.hazard);
      const card = document.createElement('button');
      card.type = 'button';
      card.className = 'card';
      card.style.cssText = 'text-align:left;cursor:pointer;width:100%;display:block';
      card.innerHTML = `
        <div class="row" style="align-items:flex-start;gap:var(--space-md)">
          <div class="grow">
            <div class="card-title">${truncEsc(r.title, 130)}</div>
            <div class="card-sub">${esc(r.supplier)} · ${esc(shortDate(r.date))}</div>
          </div>
          <span class="pill shrink0" style="background:${haz?.colour ?? '#857e72'};color:#fff;border-color:transparent">${esc(haz?.label ?? '—')}</span>
        </div>
      `;
      card.addEventListener('click', () => openRecall(r));
      results.append(card);
    }

    moreWrap.innerHTML = '';
    if (sorted.length > shown) {
      const more = document.createElement('button');
      more.type = 'button';
      more.className = 'btn';
      more.textContent = `Show more (${num(sorted.length - shown)} remaining)`;
      more.addEventListener('click', () => { shown += PAGE; draw(); });
      moreWrap.append(more);
    }
  }

  let timer: number | undefined;
  input.addEventListener('input', () => {
    clearTimeout(timer);
    timer = window.setTimeout(() => {
      // The view may have been torn down during the debounce — the router
      // replaces main's children but this timer survives, and it would then
      // write a stray ?q= onto whatever view the reader had navigated to.
      if (!host.isConnected) return;
      query = input.value;
      shown = PAGE;
      setParams({ q: query || null });
      draw();
    }, 300);
  });
  hazSel.addEventListener('change', () => {
    hazardFilter = Number(hazSel.value);
    shown = PAGE;
    setParams({ h: hazardFilter || null });
    draw();
  });

  draw();
  renderHeadline(host, stats, meta);

  // Deep link straight to a recall: #/search?r=<key>
  const key = route.params.get('r');
  if (key) {
    const hit = recalls.find((r) => r.key === key);
    if (hit) openRecall(hit);
  }
}

/**
 * The finding, stated under the search box rather than on a chart page nobody
 * reaches. Every figure comes from the pipeline, so it cannot drift from the
 * charts that show the same thing.
 */
function renderHeadline(host: HTMLElement, stats: ReturnType<typeof getBundle>['stats'], meta: ReturnType<typeof getBundle>['meta']): void {
  const h = stats.headline;
  const box = document.createElement('div');
  box.className = 'card';
  box.style.marginTop = 'var(--space-2xl)';
  box.innerHTML = `
    <div class="card-title">What has changed in the register</div>
    <div class="card-sub">Computed across all ${num(meta.accc.total)} recalls, not a sample.</div>
    <div class="stat-row" style="margin-top:var(--space-lg)">
      <div class="stat">
        <div class="stat-value">${h.buttonBatteryFromShare}% → ${h.buttonBatteryToShare}%</div>
        <div class="stat-label">${glossaryButton('buttonBattery', 'Button battery')} share of recalls</div>
        <div class="stat-note">${h.buttonBatteryFromYear} → ${h.buttonBatteryToYear}</div>
      </div>
      <div class="stat">
        <div class="stat-value">${num(h.peakTotal)} → ${num(h.latestFullTotal)}</div>
        <div class="stat-label">Recalls a year</div>
        <div class="stat-note">peak ${h.peakYear}, latest full year ${h.latestFullYear}</div>
      </div>
    </div>
    <p class="small" style="margin-top:var(--space-md)">Australia is not recalling more things than it used to —
    it is recalling a different set of things.
    <button type="button" class="glossary-link" id="see-trend">See how the mix changed</button>.</p>
  `;
  host.append(box);
  box.querySelector('#see-trend')!.addEventListener('click', () => navigate('trend'));
}
